import { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import i18n from './i18n';

import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import ResumePage from './pages/ResumePage';
import InternshipsPage from './pages/InternshipsPage';
import CareerCoachPage from './pages/CareerCoachPage';
import AnalyticsPage from './pages/AnalyticsPage';
import ProfilePage from './pages/ProfilePage';
import JobsPage from './pages/JobsPage';
import CreditsPage from './pages/CreditsPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import StudyBuddyPage from './pages/StudyBuddyPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import InterviewSimulatorPage from './pages/InterviewSimulatorPage';
import OnboardingTour from './components/OnboardingTour';
import TermsPage from './pages/TermsPage';
import SettingsPage from './pages/SettingsPage';
import AdminPage from './pages/AdminPage';

function ProtectedRoute({ children }) {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (!user) return <Navigate to="/" />;
    return children;
}

function PublicHome() {
    const { user, loading } = useAuth();
    if (loading) return null;
    if (user) return <Navigate to="/dashboard" />;
    return <LandingPage />;
}

function AppRoutes() {
    const { user } = useAuth();
    const [showTour, setShowTour] = useState(false);

    useEffect(() => {
        if (user) {
            const tourKey = `tour_completed_${user.id}`;
            if (!localStorage.getItem(tourKey)) {
                const timer = setTimeout(() => setShowTour(true), 800);
                return () => clearTimeout(timer);
            }
        }
    }, [user]);

    return (
        <>
            <Routes>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/verify-email" element={<VerifyEmailPage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/jobs" element={<JobsPage />} />
                <Route path="/study" element={<StudyBuddyPage />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/credits" element={
                    <ProtectedRoute><CreditsPage /></ProtectedRoute>
                } />
                <Route path="/settings" element={
                    <ProtectedRoute><SettingsPage /></ProtectedRoute>
                } />
                <Route path="/" element={<PublicHome />} />
                <Route path="/landing" element={<LandingPage />} />
                <Route path="/dashboard" element={
                    <ProtectedRoute><DashboardPage /></ProtectedRoute>
                } />
                <Route path="/resumes" element={
                    <ProtectedRoute><ResumePage /></ProtectedRoute>
                } />
                <Route path="/internships" element={
                    <ProtectedRoute><InternshipsPage /></ProtectedRoute>
                } />
                <Route path="/career" element={
                    <ProtectedRoute><CareerCoachPage /></ProtectedRoute>
                } />
                <Route path="/analytics" element={
                    <ProtectedRoute><AnalyticsPage /></ProtectedRoute>
                } />
                <Route path="/profile" element={
                    <ProtectedRoute><ProfilePage /></ProtectedRoute>
                } />
                <Route path="/interview" element={
                    <ProtectedRoute><InterviewSimulatorPage /></ProtectedRoute>
                } />
                <Route path="*" element={<Navigate to="/dashboard" />} />
                <Route path="/admin" element={
                    <ProtectedRoute><AdminPage /></ProtectedRoute>
                } />
            </Routes>

            {showTour && user && (
                <OnboardingTour
                    onComplete={() => setShowTour(false)}
                    userId={user.id}
                />
            )}
        </>
    );
}

function App() {
    return (
        <HelmetProvider>
            <BrowserRouter>
                <AuthProvider>
                    <AppRoutes />
                </AuthProvider>
            </BrowserRouter>
        </HelmetProvider>
    );
}

export default App;